import { compareStrings } from '@lib/utils';

export type StructType =
  | 'country'
  | 'province'
  | 'region'
  | 'constituency'
  | 'municipality'
  | 'ward'
  | 'votingdistrict';

export function isStructType(type: string): type is StructType {
  return Object.prototype.hasOwnProperty.call(structConf, type);
}

export interface StructParams {
  type: StructType;
  code: string;
}

export interface StructureBase extends StructParams {
  name: string;
}

export interface StructureWithParents extends StructureBase {
  parents: StructureBase[];
}

interface StructConf {
  displayTitle: string;
  children: StructType[];
  color?: string;
}

export const structConf: Record<StructType, StructConf> = {
  country: {
    displayTitle: 'Country',
    children: ['province'],
    color: '#d9d9d9',
  },
  province: {
    displayTitle: 'Province',
    children: ['region', 'constituency', 'municipality'],
    color: '#bebada',
  },
  region: {
    displayTitle: 'Region',
    children: ['constituency'],
    color: '#b3de69',
  },
  constituency: {
    displayTitle: 'Constituency',
    children: ['ward'],
    color: '#80b1d3',
  },
  municipality: {
    displayTitle: 'Municipality',
    children: ['ward'],
    color: '#fb8072',
  },
  ward: {
    displayTitle: 'Ward',
    children: ['votingdistrict'],
    color: '#fdb462',
  },
  votingdistrict: {
    displayTitle: 'VD',
    children: [],
  },
};

export function getPreferredChildType({
  type,
  code,
}: StructParams): StructType | undefined {
  if (type === 'province') {
    return code === 'GP' || code === 'WC' ? 'region' : 'constituency';
  } else {
    return structConf[type].children[0];
  }
}

export function filterAndSortChildren<T extends StructureBase>(
  type: StructType,
  children: T[]
): T[] {
  let sortedChildren = [...children];
  if (type === 'province') {
    const hasRegions = children.some(c => c.type === 'region');
    sortedChildren = children.filter(
      c => c.type === (hasRegions ? 'region' : 'constituency')
    );
    sortedChildren.sort((a, b) => compareStrings(a.name, b.name));
  } else if (type === 'constituency' || type === 'municipality') {
    sortedChildren.sort((a, b) => compareStrings(a.code, b.code));
  } else {
    sortedChildren.sort((a, b) => compareStrings(a.name, b.name));
  }
  return sortedChildren;
}

export function hasParent(
  structure: StructureWithParents,
  { type, code }: StructParams
): boolean {
  return (
    (structure.type === type && structure.code === code) ||
    structure.parents.some(p => p.type === type && p.code === code)
  );
}
